import { useState, useRef } from "react";
import { Info, Loader2 } from "lucide-react";
import { toggleOlineReservations } from "../../lib/api/reservations.apis";

interface OnlineReservationsToggleProps {
  restaurantId: string;
  initialValue: boolean;
  onChange?: (newValue: boolean) => void;
}

export default function OnlineReservationsToggle({
  restaurantId,
  initialValue,
}: OnlineReservationsToggleProps) {
  const [enabled, setEnabled] = useState<boolean>(initialValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  async function handleToggle() {
    setLoading(true);
    setError(null);

    try {
      const res = await toggleOlineReservations(restaurantId, { allowOnlineReservations: !enabled });
      if (res.success) {
        setEnabled(!enabled);
      }
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 py-1.5 px-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
      {/* Label + tooltip */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-medium text-gray-800 truncate">
          Online Reservations
        </span>


        <div
          className="relative shrink-0"
          ref={tooltipRef}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <button
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors flex items-center"
            aria-label="Learn more about online reservations"
            tabIndex={0}
          >
            <Info className="w-3.5 h-3.5" />
          </button>

          {showTooltip && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-30">
              <div className="w-48 bg-gray-900 text-white text-[11px] rounded-lg px-2.5 py-2 shadow-xl leading-relaxed text-center relative">
                Allow customers to make online reservations through Gemio.{" "}
                <a
                  href="https://gemio.app/about-gemio#online-reservations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-amber-300 hover:text-amber-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn more
                </a>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <span className="text-xs text-red-500 truncate">{error}</span>
        )}
      </div>

      {/* Toggle */}
      <button
        onClick={handleToggle}
        disabled={loading}
        aria-checked={enabled}
        role="switch"
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2
          ${enabled ? "bg-amber-400" : "bg-gray-200"}
          ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm flex items-center justify-center transition-transform duration-200
            ${enabled ? "translate-x-5" : "translate-x-0"}
          `}
        >
          {loading && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
        </span>
      </button>
    </div>
  );
}